import type { ChartType } from '../../models/chart.types'

import type { ChartLoadingShape } from '@/shared/ui/chart'

/**
 * Which skeleton the chart pane draws while its data is in flight. v3 picked a per-chart skeleton
 * file at the call site (`OrphexChart.vue`'s `#skeleton` slot: `LineChartSkeleton`,
 * `BarChartSkeleton`, `PieChartSkeleton`, `BubbleChartSkeleton`); module 02 now owns the
 * silhouettes, so all that is left here is the type → shape decision, kept as an exhaustive record
 * so a new renderer cannot ship without one. The mapping is v3's verbatim: only `line`/`area`/
 * `stacked_area` drew lines, only `pie`/`donut` drew the ring, only `bubble`/`scatter` drew
 * bubbles, and every other type fell through to the column silhouette.
 */
const CHART_LOADING_SHAPES: Record<ChartType, ChartLoadingShape> = {
  line: 'line',
  area: 'line',
  stacked_area: 'line',
  multi_line: 'bar',
  scatter: 'bubble',
  bubble: 'bubble',
  packed_bubble: 'bar',
  radar: 'bar',
  combination: 'bar',
  pie: 'pie',
  multi_pie: 'bar',
  donut: 'pie',
  sunburst: 'bar',
  gauge: 'bar',
  compare_column: 'bar',
  compare_bar: 'bar',
  multi_metric: 'bar',
  dumbbell: 'bar',
  bullet: 'bar',
  diverging_bar: 'bar',
  column: 'bar',
  bar: 'bar',
  stacked_column: 'bar',
  stacked_bar: 'bar',
  heatmap: 'bar',
  treemap: 'bar',
  funnel: 'bar',
  waterfall: 'bar',
  sankey: 'bar',
  lollipop: 'bar',
}

/** Falls back to the column silhouette — the shape most report charts settle on. */
export function chartLoadingShape(type: ChartType | null | undefined): ChartLoadingShape {
  return type ? (CHART_LOADING_SHAPES[type] ?? 'bar') : 'bar'
}
